// W2Cook Billing Dashboard — Connectors management tab


import type { Service } from "../data/services";
import type { ConnectorTemplate, LiveResult } from "../connectors/templates";
import { tokens as T } from "../theme/tokens";
import { Btn, Pill } from "./ui";

interface Props {
  services:           Service[];
  connectorTemplates: ConnectorTemplate[];
  connectorCfgs:      Record<string, Record<string, string>>;
  liveData:           Record<string, LiveResult>;
  fetching:           Record<string, boolean>;
  onConfigure:        (templateId: string) => void;
  onFetchOne:         (templateId: string) => void;
  onAddConnector:     () => void;
  onRemoveCustom:     (templateId: string) => void;
}

export function ConnectorsView({
  services, connectorTemplates, connectorCfgs,
  liveData, fetching,
  onConfigure, onFetchOne, onAddConnector, onRemoveCustom,
}: Props) {
  const configuredCount = Object.keys(connectorCfgs).length;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>API Connectors</div>
          <div style={{ fontSize: 13, color: T.textMuted }}>
            {configuredCount} configured · {connectorTemplates.length} templates · credentials stored locally or in Firestore
          </div>
        </div>
        <Btn variant="primary" onClick={onAddConnector}>+ Add Connector</Btn>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {connectorTemplates.map((tpl) => {
          const cfg     = connectorCfgs[tpl.id];
          const live    = liveData[tpl.id];
          const linked  = services.filter((s) => s.apiConnectorId === tpl.id);

          return (
            <div key={tpl.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 24 }}>{tpl.icon}</span>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
                      {tpl.name}
                      {tpl._custom && (
                        <span style={{ fontSize: 10, padding: "1px 6px", background: `${T.purple}20`, border: `1px solid ${T.purple}40`, borderRadius: 4, color: T.purple }}>
                          CUSTOM
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: T.textDim, marginTop: 3 }}>
                      {linked.length > 0
                        ? `Linked: ${linked.map((s) => s.name).join(", ")}`
                        : "Not linked to any service"}
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  {live     && <Pill label={live.ok ? "live" : "error"} color={live.ok ? T.sage : T.ember} />}
                  {cfg      ? <Pill label="configured" color={T.blue} /> : <Pill label="no credentials" color={T.textDim} />}
                  {fetching[tpl.id] && <span style={{ fontSize: 11, color: T.textDim }}>polling…</span>}

                  <Btn variant="subtle" onClick={() => onConfigure(tpl.id)} style={{ fontSize: 12, padding: "6px 12px" }}>
                    {cfg ? "Edit Credentials" : "Configure"}
                  </Btn>
                  {cfg && (
                    <Btn variant="ghost" onClick={() => onFetchOne(tpl.id)} style={{ fontSize: 12, padding: "6px 10px" }}>↻</Btn>
                  )}
                  {tpl._custom && (
                    <Btn variant="danger" onClick={() => onRemoveCustom(tpl.id)} style={{ fontSize: 12, padding: "6px 10px" }}>
                      Remove
                    </Btn>
                  )}
                </div>
              </div>

              {live?.note && (
                <div style={{ marginTop: 12, padding: "8px 12px", background: T.surfaceAlt, borderRadius: 8, fontSize: 12, color: live.ok ? T.textMuted : T.ember }}>
                  {live.note}
                  <span style={{ color: T.textDim }}> · {new Date(live.fetchedAt).toLocaleTimeString()}</span>
                </div>
              )}

              <div style={{ marginTop: 10, fontSize: 11, color: T.textDim }}>
                Fields: {tpl.fields.map((f) => f.label).join(" · ")}
                {" · "}
                <a href={tpl.docsUrl} target="_blank" rel="noopener noreferrer" style={{ color: T.blue }}>
                  API docs ↗
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
